import cx from 'classnames';
import dayjs from 'dayjs';
import { get } from 'lodash';
import isObject from 'lodash/isObject';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import { useDebouncedCallback } from 'use-debounce';
import { DATE_FORMAT, US_DATE_FORMAT } from '../../../../constants';
import { FORM_ROLES } from '../../../../constants/roles';
import { useFormUser } from '../../../../container/onBehalfContext';
import { useIsMobile } from '../../../../hook/useIsMobile';
import { useIsRequiredElement } from '../../../../hook/useIsRequiredElement';
import { useUserFormRole } from '../../../../hook/useUserFormRole';
import Multiline from '../../../from-builder/elements/multiline';
import { TABLE_COLUMN_MIN_WIDTH } from '../../../from-builder/elements/table-element';
import { TableWithStickyHeader } from '../../TableWithStickyHeader';
import LoginModal from '../login-modal';
import NewRowButton from './components/NewRowButton';
import { NumberModal } from './components/number-table-modal';
import './styles.scss';
import DropdownCol from './components/Dropdown';
import SignatureTableField from './components/Signature';
import DateCalendar from '../../../calendars/DateCalendar/DateCalendar';

export const TableElement = function FromElement({
  element,
  handlerChange,
  addNewRow,
  handlerChangeLabel,
  isPrint,
  highlightPrevious,
  formHasOnlyOneSubmitter,
  initiatorID,
  isPreview,
}) {
  const { rows, options } = element ?? {};
  const initiatorCanEdit = !!options.initiatorCanEdit;
  const isRequiredElement = useIsRequiredElement(element, formHasOnlyOneSubmitter);
  const userFormRole = useUserFormRole();
  const scrollRow = useRef();
  const tableWithStickyHeader = useRef(null);
  // The on-behalf submitter when the form is filled in from a "Sent" row of the
  // Submissions folder, the logged-in user otherwise: the "current user" column
  // signs for whoever the submission is recorded as.
  const user = useFormUser();
  const isMobile = useIsMobile();
  const addRowButtonHideTimeoutRef = useRef(null);
  const rowsLength = rows?.length ?? 0;

  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndexCol, setSelectedIndexCol] = useState(null);
  const [selectedIndexRow, setSelectedRow] = useState(null);
  const [currentCol, setCurrentCol] = useState(null);
  const [activeCol, setActiveCol] = useState(null);
  const [activeRow, setActiveRow] = useState(null);
  const [isOpenLogin, setIsOpenLogin] = useState(false);
  const [isFixedAutoScroll, setIsFixedAutoScroll] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // States of the button to add a new row between other rows
  const [hoveredRowIndex, setHoveredRowIndex] = useState(null);
  const [addNewRowButtonPositions, setAddNewRowButtonPositions] = useState([]);

  const formattedRows = useMemo(() => {
    let newRows = [];
    if (rowsLength && !isPrint) {
      if (options.addNewRowInsideTable && !isPreview) {
        newRows = rows.reduce((acc, row, i) => {
          acc.push({ ...row, index: i });
          if (i < rowsLength - 1) {
            const newRowId = 'new' + new Date().getTime() + i;
            const newRow = {
              label: '',
              newRow: true,
              id: newRowId,
              cols: [],
              asAddNewRowButton: true,
            };
            acc.push(newRow);
          }
          return acc;
        }, []);
      } else {
        newRows = rows.map((row, i) => ({ ...row, index: i }));
      }
    }
    return newRows;
  }, [rowsLength, isPrint, options.addNewRowInsideTable, rows]);

  const calculateAddNewRowButtonPositions = useCallback(() => {
    if (formattedRows?.length) {
      if (tableWithStickyHeader.current) {
        const tableContainerRect = tableWithStickyHeader.current.getBoundingClientRect();
        const trs = tableWithStickyHeader.current.querySelectorAll('tbody tr');
        const newPositions = [];

        trs.forEach((tr, index) => {
          const row = formattedRows[index];
          if (row?.asAddNewRowButton) {
            const trRect = tr.getBoundingClientRect();
            const topPosition = trRect.top + window.scrollY + trRect.height / 2;
            let leftPosition;

            if (isMobile) {
              leftPosition = tableContainerRect.left + tableContainerRect.width - 6;
            } else {
              leftPosition = tableContainerRect.left + tableContainerRect.width + 20;
            }

            newPositions.push({
              top: topPosition,
              left: leftPosition,
              rowIndex: index,
            });
          }
        });

        setAddNewRowButtonPositions(newPositions);
      }
    }
  }, [formattedRows, isMobile]);

  const debouncedResize = useDebouncedCallback(() => {
    calculateAddNewRowButtonPositions();
  }, 100);

  useLayoutEffect(() => {
    calculateAddNewRowButtonPositions();

    window.addEventListener('resize', debouncedResize);

    return () => {
      window.removeEventListener('resize', debouncedResize);
    };
  }, [calculateAddNewRowButtonPositions, debouncedResize]);

  const handleRowMouseEnter = (index) => {
    clearTimeout(addRowButtonHideTimeoutRef.current);
    setHoveredRowIndex(index);
  };

  const handleRowMouseLeave = () => {
    // Delay hiding the button to give time to move the cursor to the button
    addRowButtonHideTimeoutRef.current = setTimeout(() => {
      setHoveredRowIndex(null);
    }, 500);
  };

  const handleAddRowButtonMouseEnter = () => {
    clearTimeout(addRowButtonHideTimeoutRef.current); // Stop hiding the button
  };

  const handleAddRowButtonMouseLeave = () => {
    addRowButtonHideTimeoutRef.current = setTimeout(() => {
      setHoveredRowIndex(null); // Hide the button after cursor leaves
    }, 300);
  };

  const handelCloseModal = () => {
    setIsOpen(false);
    setSelectedIndexCol(null);
    setSelectedRow(null);
    setCurrentCol(null);
  };

  const openModal = () => {
    setIsOpenLogin(true);
  };

  const closeModal = () => {
    setIsOpenLogin(false);
    setActiveCol(null);
    setActiveRow(null);
  };

  const handelChangeLogin = (userInfo) => {
    handlerChange(element, userInfo, true, activeCol, activeRow);
    closeModal();
  };

  const addCurrentUserInfo = (element, isTable, colIndex, indexRow) => {
    const options = get(element, `cols[${colIndex}].options`, []);
    let userInfo = '';

    options.forEach((option) => {
      switch (option.value) {
        case 'name': {
          if (user.first_name) {
            userInfo = userInfo + `${user.first_name}`;
          }
          break;
        }
        case 'surname': {
          if (user.last_name) {
            userInfo = userInfo + ` ${user.last_name}`;
          }
          break;
        }
        case 'position': {
          if (user?.position?.name) {
            userInfo = userInfo + ` ${user?.position?.name}`;
          }
          break;
        }
        case 'date': {
          userInfo = userInfo + ` ${dayjs(new Date()).format(DATE_FORMAT)}`;
          break;
        }
        case 'time': {
          userInfo = userInfo + ` ${dayjs(new Date()).format('hh:mm a')}`;
          break;
        }
      }
    });

    handlerChange(element, userInfo, isTable, colIndex, indexRow);
  };

  useEffect(() => {
    if (!isFixedAutoScroll && element) {
      handlerChange(element);
      setIsFixedAutoScroll(true);
    }
  }, [element, handlerChange, isFixedAutoScroll]);

  useEffect(() => {
    if (element.options.enableScroll && isFixedAutoScroll && !isScrolled && scrollRow.current) {
      setTimeout(() => {
        const reactModalOverlayElements = document.querySelectorAll('.ReactModal__Overlay');
        const refBoundingClientRectTop = scrollRow.current.getBoundingClientRect().top;
        if (reactModalOverlayElements.length) {
          reactModalOverlayElements[0].scrollTo(0, refBoundingClientRectTop - 44);
        } else {
          const scrollTopValue = refBoundingClientRectTop - 200; // header height + actions height = 200
          window.scrollTo(0, scrollTopValue);
        }
      }, 100);

      setTimeout(() => {
        setIsScrolled(true);
      }, 100);
    }
  }, [element, isFixedAutoScroll, isScrolled]);

  const renderDateCol = (row, colIndex) => {
    let layout = null;
    const col = element?.cols[colIndex];

    if (col && col.type?.value === 'date') {
      const { editable, editRole } = col;
      const rowCol = get(row, `cols[${colIndex}]`, {});
      const { value, readOnly } = rowCol;
      const rowIndex = rows?.findIndex((r) => r.id === row.id);
      const selected = value ? new Date(value) : null;
      const isActive = !readOnly && !isPrint && editable && (!editRole || editRole === userFormRole || formHasOnlyOneSubmitter);
      layout = (
        <DateCalendar
          selectedDate={selected}
          readOnly={!isActive}
          className={cx('form-control', {
            readonly: !isActive,
            'highlight-date-picker': rowCol?.user_metadata?.can_edit_previous_submissions === false,
          })}
          popperProps={isPrint ? undefined : { strategy: 'fixed' }}
          onInputClick={() => {
            if (isActive && !selected) {
              const currentTime = new Date();
              handlerChange(element, currentTime, true, colIndex, rowIndex);
            }
          }}
          handleChange={(date) => isActive && handlerChange(element, date, true, colIndex, rowIndex)}
        />
      );
    }
    return layout;
  };

  const renderSingleLineCol = (row, col) => {
    let layout = null;
    if (col && col.type?.value === 'single-line') {
      const colIndex = row?.cols?.findIndex((c) => c.id === col.id);
      const rowCol = get(row, `cols[${colIndex}]`);
      if (rowCol) {
        const rowIndex = element?.rows.findIndex((r) => r.id === row.id);
        const { value = '', readOnly, filledByRole } = rowCol;
        const isActive =
          !readOnly &&
          !isPrint &&
          (!filledByRole ||
            (initiatorCanEdit && (userFormRole == FORM_ROLES.FORM_INITIATOR || formHasOnlyOneSubmitter || initiatorID === user.id)));

        layout = (
          <input
            type="text"
            value={value}
            readOnly={!isActive}
            className={cx('form-control', { 'highlight-single-line': rowCol?.user_metadata?.can_edit_previous_submissions === false })}
            placeholder={'Enter Text'}
            onChange={(e) => isActive && handlerChange(element, e.target.value, true, colIndex, rowIndex)}
          />
        );
      }
    }

    return layout;
  };

  const renderTimeCol = (row, colIndex) => {
    let layout = null;
    if (rows) {
      const col = element?.cols[colIndex];
      if (col && col.type?.value === 'time') {
        const rowIndex = rows.findIndex((r) => r.id === row.id);
        const rowCol = get(row, `cols[${colIndex}]`, {});
        const { editable, editRole } = col;
        const { value, readOnly } = rowCol;
        const selected = value ? new Date(value) : null;
        const isActive = !readOnly && !isPrint && editable && (!editRole || editRole === userFormRole || formHasOnlyOneSubmitter);

        layout = (
          <DatePicker
            selected={selected}
            readOnly={!isActive}
            timeIntervals={60}
            dateFormat="h:mm aa"
            placeholderText={'Choose Time'}
            className={cx('form-control', {
              readonly: !isActive,
              'highlight-time-picker': rowCol?.user_metadata?.can_edit_previous_submissions === false,
            })}
            popperContainer={({ children }) => null}
            onInputClick={() => {
              if (isActive && !selected) {
                const currentTime = new Date();
                handlerChange(element, currentTime, true, colIndex, rowIndex);
              }
            }}
            onChange={(date) => isActive && handlerChange(element, date, true, colIndex, rowIndex)}
          />
        );
      }
    }
    return layout;
  };

  const renderDropdownCol = (row, col) => {
    let layout = null;
    if (col && col.type?.value === 'dropdown') {
      const { options = [] } = col;
      const optionsLength = options.length;
      const colIndex = row?.cols?.findIndex((c) => c.id === col.id);
      const rowCol = get(row, `cols[${colIndex}]`);

      if (rowCol) {
        const rowIndex = element.rows.findIndex((r) => r.id === row.id);
        const { value } = rowCol;
        // The default option belongs to the column: a cell can hold a stale copy of a
        // default that has since been cleared, so never fall back to the cell's own.
        const { defaultOptionId } = col;
        // Or readonly

        const isActive = !isPrint;

        let selectedOption;

        if (isObject(value)) {
          selectedOption = value;
        } else if (defaultOptionId) {
          selectedOption = options.find((option) => option.id === defaultOptionId);
        }

        layout = (
          <Select
            menuPortalTarget={document.body}
            menuPlacement={'auto'}
            value={selectedOption}
            options={options}
            readOnly={!isActive}
            classNamePrefix="react-select"
            className={cx('form-control-select qwe', {
              'highlight-dropdown': rowCol?.user_metadata?.can_edit_previous_submissions === false,
            })}
            styles={{
              menuList: (provided) => ({
                ...provided,
                height: '100%',
                minHeight: optionsLength >= 3 ? 168 : undefined,
              }),
              menuPortal: (base) => ({ ...base, zIndex: 9 }),
            }}
            onChange={(e) => isActive && handlerChange(element, e, true, colIndex, rowIndex)}
          />
        );
      }
    }
    return layout;
  };

  const renderMultilineCol = (row, col) => {
    let layout = null;
    if (col && col.type?.value === 'multiline') {
      const colIndex = row?.cols?.findIndex((c) => c.id === col.id);
      const rowCol = get(row, `cols[${colIndex}]`);
      if (rowCol) {
        const rowIndex = element?.rows.findIndex((r) => r.id === row.id);
        const { value = '', readOnly, filledByRole } = rowCol;
        const isActive =
          !readOnly &&
          !isPrint &&
          (!filledByRole ||
            (initiatorCanEdit && (userFormRole == FORM_ROLES.FORM_INITIATOR || formHasOnlyOneSubmitter || initiatorID === user.id)));
        layout = (
          <Multiline
            value={value}
            readOnly={!isActive}
            placeholder={'Enter Text'}
            classnames={cx({ 'highlight-multiline': rowCol?.user_metadata?.can_edit_previous_submissions === false })}
            onChange={(e) => isActive && handlerChange(element, e.target.value, true, colIndex, rowIndex)}
          />
        );
      }
    }

    return layout;
  };

  const getMaxDecimals = (colId) => {
    let max = 0;
    element.rows.forEach((row) => {
      const cell = row.cols.find((c) => c.id === colId);
      const value = cell?.value;
      if (value && !isNaN(value)) {
        const decimals = value.toString().split('.')[1]?.length || 0;
        if (decimals > max) max = decimals;
      }
    });
    return max;
  };

  const renderTableFooter = () => {
    const hasTotals = element.cols.some((col) => col.type?.value === 'number' && col.countTotal);

    if (!hasTotals) return '';

    return (
      <tr>
        {!element.options.showQuestions && <td className="first-section"></td>}

        {element.cols.map((col) => {
          if (!(col.type.value === 'number' && col.countTotal)) {
            return <td key={col.id} className="count-total-col"></td>;
          }

          const total = element.rows.reduce((acc, row) => {
            const cell = row.cols.find((c) => c.id === col.id);
            const value = parseFloat(cell?.value);
            return acc + (isNaN(value) ? 0 : value);
          }, 0);

          const decimals = getMaxDecimals(col.id);
          const formatted = decimals === 0 ? Math.round(total) : total.toFixed(decimals);

          return (
            <td key={col.id} className="count-total-col">
              {col.countTotalLabel ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center' }}>
                  <span className="count-total-label" style={{ textAlign: 'left' }}>
                    {col.countTotalLabel || ''}
                  </span>
                  <span className="count-total-value">{formatted}</span>
                </div>
              ) : (
                <span className="count-total-value">{formatted}</span>
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  // Where two tables overlap, the rows of each are lifted above the other one's header.
  // Print stacks them the same way, or a submission shows the other table on top.
  const tbodyStyle = {
    zIndex: 5000 + (element.options.zIndex || 10),
    position: 'relative',
  };

  const renderNoPrintTableTBody = () => {
    return (
      <tbody style={tbodyStyle}>
        {formattedRows.map((row, rowIndex) => {
          const { asAddNewRowButton, id: rowID, index: originalRowIndex } = row;
          return (
            <tr
              key={rowID}
              ref={element.options.enableScroll && row?.scrollToRow ? scrollRow : null}
              className={cx({ scrollTo: row?.scrollToRow, asAddNewRowButton })}
              onMouseEnter={() => handleRowMouseEnter(rowIndex)}
              onMouseLeave={handleRowMouseLeave}
              onClick={() => setHoveredRowIndex(rowIndex)}
              style={!asAddNewRowButton ? { minHeight: '50px', height: '50px' } : {}}
            >
              {asAddNewRowButton ? (
                <td colSpan={100} />
              ) : (
                <>
                  {!element.options.showQuestions && (
                    <>
                      {!row.newRow ? (
                        <td className={'first-section'} style={{ width: element.options.width ?? '130px' }}>
                          {row.label}
                        </td>
                      ) : (
                        <td className={'first-section'} style={{ width: element.options.width ?? '130px' }}>
                          <input
                            type="text"
                            className="form-control"
                            value={row?.label}
                            onChange={(e) => handlerChangeLabel(element, e.target.value, originalRowIndex)}
                          />
                        </td>
                      )}
                    </>
                  )}

                  {element.cols.map((col, indexCol) => {
                    let isReadOnlyTd = row?.cols && row?.cols[indexCol]?.readOnly;
                    if (col?.type?.value === 'dropdown') {
                      isReadOnlyTd = false;
                    }
                    return (
                      <td
                        key={indexCol}
                        className={cx({
                          'current-user': col?.type?.value === 'current-user',
                          'readOnly-td': isReadOnlyTd,
                        })}
                        style={{ maxWidth: col.width }}
                      >
                        {renderSingleLineCol(row, col)}

                        {col?.type?.value === 'current-user' && (
                          <>
                            {!get(row, `cols[${indexCol}].value`) ? (
                              <button
                                className={'btn btn-outline btn-outline-secondary btn-signature'}
                                onClick={() => {
                                  addCurrentUserInfo(element, true, indexCol, originalRowIndex);
                                }}
                              />
                            ) : (
                              <p
                                className={cx({
                                  'highlight-current-user': row?.cols?.[indexCol]?.user_metadata?.can_edit_previous_submissions === false,
                                })}
                              >
                                {row?.cols[indexCol]?.value}
                              </p>
                            )}
                          </>
                        )}

                        {renderDateCol(row, indexCol)}
                        {renderTimeCol(row, indexCol)}
                        {col && col.type?.value === 'dropdown' && (
                          <DropdownCol row={row} col={col} element={element} isPrint={isPrint} handlerChange={handlerChange} />
                        )}
                        {renderMultilineCol(row, col)}

                        {col?.type?.value === 'number' && (
                          <input
                            type="text"
                            className={cx('form-control', {
                              'highlight-number': row?.cols?.[indexCol]?.user_metadata?.can_edit_previous_submissions === false,
                            })}
                            readOnly
                            value={row?.cols ? row?.cols[indexCol]?.value ?? '' : ''}
                            onClick={async () => {
                              if (!get(row?.cols, `[${indexCol}].readOnly`)) {
                                setSelectedIndexCol(indexCol);
                                setSelectedRow(originalRowIndex);
                                if (row.cols) {
                                  setCurrentCol(row?.cols[indexCol] ? row?.cols[indexCol] : '');
                                } else {
                                  handlerChange(element, '', true, selectedIndexCol, selectedIndexRow);
                                  setCurrentCol(true);
                                }
                                setIsOpen(true);
                              }
                            }}
                          />
                        )}

                        {col.type?.value === 'electronic-signature' && (
                          <input
                            type="text"
                            className={cx('form-control', {
                              'highlight-signature': row?.cols?.[indexCol]?.user_metadata?.can_edit_previous_submissions === false,
                            })}
                            readOnly={true}
                            value={row?.cols ? row?.cols[indexCol]?.value : ''}
                            onClick={() => {
                              if (!get(row?.cols, `[${indexCol}].readOnly`)) {
                                if (!row?.cols) {
                                  handlerChange(element, '', true, indexCol, originalRowIndex);
                                  openModal();
                                  setActiveRow(originalRowIndex);
                                  setActiveCol(indexCol);
                                } else {
                                  if (!row?.cols[indexCol]?.value) {
                                    openModal();
                                    setActiveRow(originalRowIndex);
                                    setActiveCol(indexCol);
                                  }
                                }
                              }
                            }}
                          />
                        )}

                        {col.type?.value === 'signature' && (
                          <SignatureTableField
                            value={row?.cols ? row?.cols[indexCol]?.value : ''}
                            onChange={(value) => handlerChange(element, value, true, indexCol, originalRowIndex)}
                            isPrint={isPrint}
                          />
                        )}

                        {!col.type && (
                          <input
                            type="text"
                            className={cx('form-control', {
                              'highlight-text': row?.cols?.[indexCol]?.user_metadata?.can_edit_previous_submissions === false,
                            })}
                            readOnly={row?.cols ? row?.cols[indexCol]?.readOnly : false}
                            value={row?.cols ? row?.cols[indexCol]?.value : ''}
                            onChange={(e) => handlerChange(element, e.target.value, true, indexCol, originalRowIndex)}
                          />
                        )}
                      </td>
                    );
                  })}
                </>
              )}
            </tr>
          );
        })}
        {renderTableFooter()}
      </tbody>
    );
  };

  return (
    <>
      <div
        className={cx('form-control-table-wrapper', {
          'form-control-table-wrapper-print': isPrint,
          [`alignment--${element.options.tableAlignment}`]: element.options.tableAlignment,
        })}
      >
        <div id={element.id} className="form-control-table-responsive">
          <label htmlFor={element.id} className="form-label w-full">
            {element.options.label} <span className="required">{isRequiredElement ? '*' : ''}</span>
          </label>
          {!isPrint && (
            <TableWithStickyHeader ref={tableWithStickyHeader}>
              <thead>
                <tr style={{ minHeight: '50px', height: '50px' }}>
                  {!element.options.showQuestions && (
                    <th
                      className={'first-section'}
                      style={{
                        minWidth: TABLE_COLUMN_MIN_WIDTH,
                        width: element.options.width ?? '130px',
                        maxWidth: element.options.width,
                      }}
                    >
                      {typeof element.options.rowHeaderTitle === 'string' ? element.options.rowHeaderTitle : 'Question'}
                    </th>
                  )}
                  {element.cols.map((col) => (
                    <th id={col.id} key={col.id} style={{ width: col.width, minWidth: TABLE_COLUMN_MIN_WIDTH, maxWidth: col.width }}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              {renderNoPrintTableTBody()}
            </TableWithStickyHeader>
          )}

          {isPrint && (
            <TableWithStickyHeader isPrint={isPrint}>
              <thead>
                <tr style={{ minHeight: '50px', height: '50px' }}>
                  {!element.options.showQuestions && (
                    <th
                      className={'first-section'}
                      style={{
                        minWidth: TABLE_COLUMN_MIN_WIDTH,
                        width: element.options.width ?? '130px',
                        maxWidth: element.options.width,
                      }}
                    >
                      {typeof element.options.rowHeaderTitle === 'string' ? element.options.rowHeaderTitle : 'Question'}
                    </th>
                  )}
                  {element.cols.map((col) => (
                    <th id={col.id} key={col.id} style={{ width: col.width, minWidth: TABLE_COLUMN_MIN_WIDTH, maxWidth: col.width }}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody style={tbodyStyle}>
                {element.rows.map((row, indexRow) => (
                  <tr
                    style={{ minHeight: '50px', height: '50px' }}
                    key={indexRow}
                    ref={element.options.enableScroll && row?.scrollToRow ? scrollRow : null}
                    className={cx({ scrollTo: row?.scrollToRow })}
                  >
                    {!element.options.showQuestions && (
                      <>
                        {!row.newRow && (
                          <td className={'first-section'} style={{ width: element.options.width ?? '130px' }}>
                            {row.label}
                          </td>
                        )}
                        {row.newRow && (
                          <td className={'first-section'} style={{ width: element.options.width ?? '130px' }}>
                            <input
                              type="text"
                              className="form-control"
                              value={row?.label}
                              onChange={(e) => handlerChangeLabel(element, e.target.value, indexRow)}
                            />
                          </td>
                        )}
                      </>
                    )}
                    {element.cols.map((cols, indexCol) => {
                      const colType = cols?.type?.value;
                      const rowColValue = get(row, `cols[${indexCol}].value`);
                      return (
                        <td
                          key={indexCol}
                          style={{
                            width: cols.width,
                          }}
                          className={`${cols?.type?.value === 'current-user' ? 'current-user' : ''}`}
                        >
                          {cols?.type?.value === 'signature' ? (
                            <SignatureTableField value={rowColValue} isPrint={isPrint} />
                          ) : (
                            <>
                              {isPrint && cols?.type?.value !== 'date' && cols?.type?.value !== 'time' && row?.cols
                                ? row?.cols[indexCol] &&
                                  typeof row?.cols[indexCol].value === 'object' &&
                                  row?.cols[indexCol].value !== null &&
                                  row?.cols[indexCol].value.hasOwnProperty('id')
                                  ? row?.cols[indexCol]?.value.otherInputValue || row?.cols[indexCol]?.value.label
                                  : row?.cols[indexCol]?.value
                                : ''}

                              {isPrint && colType === 'date' && rowColValue && rowColValue !== ''
                                ? dayjs(new Date(rowColValue)).format(DATE_FORMAT)
                                : undefined}

                              {isPrint && colType === 'time' && rowColValue && rowColValue !== ''
                                ? dayjs(new Date(rowColValue)).format('hh:mm a')
                                : undefined}

                              {!isPrint && (
                                <>
                                  {cols?.type?.value === 'current-user' && (
                                    <>
                                      {row?.cols && !row?.cols[indexCol]?.value && <></>}

                                      {row?.cols && row?.cols[indexCol]?.value && <p>{row?.cols[indexCol]?.value}</p>}
                                    </>
                                  )}

                                  {renderSingleLineCol(row, cols)}
                                  {renderDateCol(row, indexCol)}
                                  {renderTimeCol(row, indexCol)}
                                  {renderDropdownCol(row, cols)}
                                  {renderMultilineCol(row, cols)}

                                  {cols?.type?.value === 'number' && (
                                    <input
                                      type="text"
                                      className="form-control"
                                      readOnly
                                      value={row?.cols ? row?.cols[indexCol]?.value ?? '' : ''}
                                      onClick={async () => {
                                        setSelectedIndexCol(indexCol);
                                        setSelectedRow(indexRow);
                                        if (row.cols) {
                                          setCurrentCol(row?.cols[indexCol] ? row?.cols[indexCol] : '');
                                        } else {
                                          handlerChange(element, '', true, selectedIndexCol, selectedIndexRow);
                                          setCurrentCol(true);
                                        }
                                        setIsOpen(true);
                                      }}
                                    />
                                  )}

                                  {cols.type?.value === 'electronic-signature' && (
                                    <input
                                      type="text"
                                      className="form-control"
                                      readOnly={true}
                                      value={row?.cols ? row?.cols[indexCol]?.value : ''}
                                      onClick={() => {
                                        if (!row?.cols) {
                                          handlerChange(element, '', true, indexCol, indexRow);
                                          openModal();
                                          setActiveRow(indexRow);
                                          setActiveCol(indexCol);
                                        } else {
                                          if (!row?.cols[indexCol]?.value) {
                                            openModal();
                                            setActiveRow(indexRow);
                                            setActiveCol(indexCol);
                                          }
                                        }
                                      }}
                                    />
                                  )}
                                  {!cols.type && (
                                    <input
                                      type="text"
                                      className="form-control"
                                      readOnly={row?.cols ? row?.cols[indexCol]?.readOnly : false}
                                      value={row?.cols ? row?.cols[indexCol]?.value : ''}
                                      onChange={(e) => handlerChange(element, e.target.value, true, indexCol, indexRow)}
                                    />
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {renderTableFooter()}
              </tbody>
            </TableWithStickyHeader>
          )}
        </div>
      </div>
      {element.options.addRowsButton && (
        <button className={'btn ml-auto d-flex'} onClick={() => addNewRow(element)}>
          <i className={'icon-plus-svgrepo-com mr-2'} /> Add Row
        </button>
      )}

      {addNewRowButtonPositions.map((item) => {
        let layout = null;
        if (hoveredRowIndex === item.rowIndex) {
          layout = (
            <NewRowButton
              key={item.rowIndex}
              top={item.top}
              left={item.left}
              onClick={() => addNewRow(element, item.rowIndex)}
              onMouseEnter={handleAddRowButtonMouseEnter}
              onMouseLeave={handleAddRowButtonMouseLeave}
            />
          );
        }
        return layout;
      })}

      {element.options.instructions && <p className="instructions">{element.options.instructions}</p>}

      {isOpen && currentCol && (
        <NumberModal
          isOpen={isOpen}
          element={element}
          currentCol={currentCol}
          selectedIndexRow={selectedIndexRow}
          selectedIndexCol={selectedIndexCol}
          handlerChange={handlerChange}
          handelCloseModal={handelCloseModal}
        />
      )}

      {isOpenLogin && (
        <LoginModal
          element={element}
          closeModal={closeModal}
          isOpen={isOpenLogin}
          handelSuccess={handelChangeLogin}
          activeCol={activeCol}
          title={'Login Modal'}
        />
      )}
    </>
  );
};
